// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Card,
  H1,
  Icon,
  ListDescription,
  ListItem,
  MdPadding,
  SecondaryPage,
  Separator,
  Tag,
  XlSpacing,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect, useState } from 'react';
import { Linking, Platform } from 'react-native';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { ASSISTANT_PRESSED_EVENTS, ASSISTANT_VIEWED_EVENTS } from '~shared/features/alto-assistant/analytics/constants';
import { ASSISTANT_PAGE_ORIGINS, type AssistantTabName } from '~shared/features/alto-assistant/constants';
import { MESSAGE_RESPONSE_TIME } from '~shared/features/messages/constants';
import { useAnalytics } from '~shared/hooks';
import { useWundercomUnreadMessages } from '~shared/hooks/alto-assistant/useWundercomUnreadMessages';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ContactUsActionSheet } from '../contact-us/ContactUsActionSheet';
import { FAQ_KEYS } from '../faq/Constants';
import { Inbox } from '../support-cases/Inbox';
import { HelpTopicsActionSheet } from '../wizard/HelpTopicsActionSheet';
import { HolidayClosureAlert } from './HolidayClosureAlert';
import { PriceIncreaseAlert } from './PriceIncreaseAlert';
import { Shortcuts } from './Shortcuts';
import { Tabs } from './Tabs';

const isWeb = Platform.OS === 'web';

export const SupportPage = () => {
  const { getParam, navigate } = useNavigation<'RouteTabMessages'>();
  const { trackEvent, trackPageView } = useAnalytics();
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  useWundercomUnreadMessages();
  const activeTabParam = getParam('activeTab', undefined);
  const originParam = getParam('origin', undefined);
  const [activeTab, setActiveTab] = useState<AssistantTabName>(activeTabParam || 'assistant');

  useEffect(() => {
    const sendOrigin = async () => {
      const initialURL = await Linking.getInitialURL();
      let origin: string | null = null;

      if (initialURL) {
        // If user came from a deep link
        origin = ASSISTANT_PAGE_ORIGINS.DEEP_LINK;
      } else if (originParam) {
        // If user was redirected with navigate
        origin = originParam;
      }

      trackPageView({
        event: EVENTS.ASSISTANT_COMPONENT_VIEWED,
        params: {
          name: ASSISTANT_VIEWED_EVENTS.ASSISTANT_LANDING_PAGE,
          origin,
        },
      });
    };

    sendOrigin();
  }, [originParam, trackPageView]);

  const holidayClosureOnPress = () => {
    navigate('RouteFaq', {
      faqKey: FAQ_KEYS.HOLIDAY_CLOSURE,
      hideBottomButtons: true,
    });
  };

  const handleContactInfoPress = () => {
    trackEvent({
      event: EVENTS.ASSISTANT_COMPONENT_PRESSED,
      params: {
        name: ASSISTANT_PRESSED_EVENTS.SHOW_CONTACT_INFORMATION,
      },
    });
    setActiveActionSheet(<ContactUsActionSheet />);
  };

  return (
    <SecondaryPage
      keyboardAvoidingEnabled={false}
      scrollEnabled
      footerPlacementContext="edgelessScreenWithTabBar"
      headerBackgroundColor={COLORS.BACKGROUND_COLORS.SECONDARY_LIGHTEST}
      HeaderContent={
        <MdPadding topPadding={isWeb ? SPACING.STATIC.XXXL : SPACING.STATIC.XS}>
          <H1>Support</H1>
          <HolidayClosureAlert onPress={holidayClosureOnPress} />
          <PriceIncreaseAlert />
          <XsSpacing />
          <Tabs
            activeTab={activeTab}
            onSetActiveTab={setActiveTab}
          />
        </MdPadding>
      }
    >
      {activeTab === 'assistant' ? (
        <>
          <Shortcuts />
          <XlSpacing />
          <Card
            title={'Get in touch'}
            illustrationSrc={getImage('helpBubbleTransparent')}
            illustrationAccessibilityLabel={IMG_ALT_TEXTS.helpBubble}
            description={'Select method below to get started'}
          >
            <ListItem
              testID="message-care-team"
              title={'Message care team'}
              descriptions={[
                <ListDescription key={'message-care-team'}>
                  {`We typically respond within ${MESSAGE_RESPONSE_TIME} during business hours.`}
                </ListDescription>,
              ]}
              onPress={() => {
                setActiveActionSheet(<HelpTopicsActionSheet />);
              }}
              tagPlacement={'title'}
              tags={
                <Tag
                  label={'Recommended'}
                  type={'recommended'}
                />
              }
              RightContent={
                <Icon
                  name={'chevronright-small'}
                  accessibilityLabel={'Press to open help categories action sheet'}
                />
              }
            />
            <Separator />
            <ListItem
              title={'Contact info'}
              onPress={handleContactInfoPress}
              RightContent={
                <Icon
                  name={'chevronright-small'}
                  accessibilityLabel={"Press to view Alto's contact info"}
                />
              }
            />
          </Card>
        </>
      ) : null}
      {activeTab === 'messages' ? <Inbox /> : null}
    </SecondaryPage>
  );
};
